import { Component, OnInit, OnDestroy } from '@angular/core'; // Removido HostListener
import { Router } from '@angular/router';
import { ConsumidorService } from '../../../services/consumidor.service';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { map, switchMap } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';

interface ConsumerResponse {
  itens: any[];
  totalItens: number;
  paginaAtual: number;
  totalPaginas: number;
  tamanhoPagina: number;
}

interface ConsumerFilters {
  nome: string;
  cpf: string;
  matricula: string;
}

@Component({
  selector: 'app-consulta-consumidor',
  standalone: false,
  templateUrl: './consulta-consumidor.component.html',
  styleUrls: ['./consulta-consumidor.component.css']
})
export class ConsultaConsumidorComponent implements OnInit, OnDestroy {
  consumers: any[] = [];
  loading: boolean = false;
  error: string | null = null;
  showModal: boolean = false;
  selectedConsumer: any = null;
  paginaAtual: number = 1;
  tamanhoPagina: number = 10;
  totalPaginas: number = 0;
  totalItens: number = 0;
  
  filters: ConsumerFilters = {
    nome: '',
    cpf: '',
    matricula: ''
  };

  filterType: 'nome' | 'cpf' | 'matricula' = 'nome';
  searchTerm: string = '';
  isSuperAdmin: boolean = false;
  role: string = ''; // Add role property
  totalPages: { [page: number]: any[] } = {}; // Cache em memória para as páginas carregadas
 
  showImageModal: boolean = false;
  imageModalSrc: string | null = null;

  private mediaKeys = ['fotos', 'assinaturas'];

  constructor(
    private consumidorService: ConsumidorService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.totalPages = {}; // Limpa o cache em memória ao iniciar o componente
    this.verificarPermissoes();
    this.loadAllConsumers(); // Carrega os dados da primeira página
  }

  verificarPermissoes(): void {
    // Recuperar perfil do usuário do localStorage
    const userData = localStorage.getItem('usuario');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        // A propriedade role está em user.role conforme mostrado na imagem
        this.role = user.role || '';
        this.isSuperAdmin = user.role === 'SuperAdministrador';
      } catch (error) {
        console.error('Erro ao verificar permissões do usuário:', error);
        this.isSuperAdmin = false;
        this.role = '';
      }
    }
  }

  ngOnDestroy(): void {
  }

  loadAllConsumers(): void {
    this.loading = true;
    this.error = null;

    this.consumidorService.consultarTodosMaisRecentes(
      this.paginaAtual,
      this.tamanhoPagina,
      this.filters.nome,
      this.filters.cpf,
      this.filters.matricula
    ).subscribe({
      next: (response: ConsumerResponse) => {
        this.consumers = response.itens;
        this.totalPaginas = response.totalPaginas;
        this.totalItens = response.totalItens;
        this.loading = false;
      },
      error: (err) => {
        console.error('Erro ao carregar consumidores:', err);
        this.error = 'Ocorreu um erro ao carregar os consumidores.';
        this.loading = false;
      }
    });
  }

  buscarTodosConsumidores(): Observable<any[]> {
  const tamanhoPagina = 50; // ou o valor padrão da sua API
  return this.consumidorService.consultarTodosMaisRecentes(1, tamanhoPagina).pipe(
    switchMap((primeiraPagina) => {
      const totalPaginas = primeiraPagina.totalPaginas;
      let requisicoes: Observable<ConsumerResponse>[] = [];

      // Página 1 já veio, falta buscar da 2 até totalPaginas
      for (let p = 2; p <= totalPaginas; p++) {
        requisicoes.push(this.consumidorService.consultarTodosMaisRecentes(p, tamanhoPagina));
      }

      if (requisicoes.length === 0) {
        // Só tem uma página
        return of(primeiraPagina.itens);
      }

      // forkJoin espera todas as requests terminarem
      return forkJoin(requisicoes).pipe(
        map((demaisPaginas) => {
          let todosItens = [...primeiraPagina.itens];
          demaisPaginas.forEach(res => todosItens.push(...res.itens));
          return todosItens;
        })
      );
    })
  );
}


  mudarPagina(pagina: number): void {
    if (pagina < 1 || pagina > this.totalPaginas || pagina === this.paginaAtual || this.totalPaginas === 0) {
      return;
    }
    
    this.paginaAtual = pagina;
    this.loadAllConsumers(); // Carregará do cache em memória se a página já foi visitada nesta sessão
    window.scrollTo(0, 0);
  }
  
  getPaginasDisponiveis(): number[] {
    const paginas: number[] = [];
    const maxBotoes = 5; // Número máximo de botões de página para exibir
    
    if (this.totalPaginas <= maxBotoes) {
      // Se houver menos páginas que o máximo, exibe todas
      for (let i = 1; i <= this.totalPaginas; i++) {
        paginas.push(i);
      }
    } else {
      // Calcula o intervalo de páginas a exibir
      let inicio = Math.max(1, this.paginaAtual - Math.floor(maxBotoes / 2));
      let fim = Math.min(this.totalPaginas, inicio + maxBotoes - 1);
      
      // Ajusta o início se necessário para mostrar sempre maxBotoes páginas
      if (fim - inicio + 1 < maxBotoes) {
        inicio = Math.max(1, fim - maxBotoes + 1);
      }
      
      for (let i = inicio; i <= fim; i++) {
        paginas.push(i);
      }
    }
    
    return paginas;
  }
  
  // Filter methods
  applyFilters(): void {
    // Atualiza somente o filtro selecionado, não limpa os outros
    this.filters = {
      nome: this.filterType === 'nome' ? this.searchTerm : '',
      cpf: this.filterType === 'cpf' ? this.searchTerm : '',
      matricula: this.filterType === 'matricula' ? this.searchTerm : ''
    };
    this.paginaAtual = 1;
    this.loadAllConsumers();
  }
  
  clearFilters(): void {
    this.filters = { nome: '', cpf: '', matricula: '' };
    this.searchTerm = '';
    this.paginaAtual = 1;
    this.loadAllConsumers();
  }

  openImageModal(src: string): void {
    this.imageModalSrc = src;
    this.showImageModal = true;
  }

  // Fecha o modal de visualização de imagem
  closeImageModal(): void {
    this.imageModalSrc = null;
    this.showImageModal = false;
  }

  deleteConsumer(consumer: any) {
    const usuarioId = this.getUserIdFromLocalStorage();
    if (!usuarioId) {
      Swal.fire('Erro', 'Usuário não identificado. Faça login novamente.', 'error');
      return;
    }

    Swal.fire({
      title: 'Confirmação',
      text: 'Tem certeza que deseja excluir este consumidor?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed && consumer.idJuncao) {
        this.consumidorService.deletarPorIdJuncao(consumer.idJuncao, usuarioId).subscribe({
          next: () => {
            Swal.fire({
              title: 'Sucesso!',
              text: 'Consumidor excluído com sucesso.',
              icon: 'success'
            });
            // Força a limpeza do cache e recarregamento para refletir a exclusão
            this.totalPages = {}; 
            this.loadAllConsumers();
          },
          error: (err) => {
            console.error('Erro ao excluir consumidor:', err);
            Swal.fire({
              title: 'Erro',
              text: 'Ocorreu um erro ao excluir o consumidor. Tente novamente.',
              icon: 'error'
            });
          }
        });
      } else if (result.isConfirmed && !consumer.idJuncao) {
        Swal.fire({
          title: 'Erro',
          text: 'ID de junção não encontrado para este consumidor.',
          icon: 'error'
        });
      }
    });
  }

  // Novo método que verifica permissões antes de excluir
  handleDeleteConsumer(consumer: any): void {
    if (!this.isSuperAdmin) {
      Swal.fire({
        title: 'Permissão Negada',
        text: 'Somente SuperAdministrador pode excluir consumidores.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }
    
    // Se chegou aqui, é SuperAdmin e pode prosseguir com a exclusão
    this.deleteConsumer(consumer);
  }

  // Helper method to format the address - showing only bairro and cidade
  formatAddress(consumer: any): string {
    // For nested address structure
    if (consumer?.endereco && typeof consumer.endereco === 'object') {
      const endereco = consumer.endereco;
      if (endereco.bairro && endereco.cidade) {
        return `${endereco.bairro}, ${endereco.cidade}`;
      } else if (endereco.bairro) {
        return endereco.bairro;
      } else if (endereco.cidade) {
        return endereco.cidade;
      }
    }
    
    // For direct address properties
    if (consumer?.bairro && consumer?.cidade) {
      return `${consumer.bairro}, ${consumer.cidade}`;
    } else if (consumer?.bairro) {
      return consumer.bairro;
    } else if (consumer?.cidade) {
      return consumer.cidade;
    }
    
    return 'Endereço não disponível';
  }

  // Helper method to determine debt status
  hasDebt(consumer: any): string {
    // Verifica se o consumidor possui débito
    return consumer.possuiDebito ? 'Sim' : 'Não';
  }

  // Add this helper method to get userId from localStorage
  private getUserIdFromLocalStorage(): string | null {
    // Try direct usuarioId first
    let usuarioId = localStorage.getItem('usuarioId');
    
    if (!usuarioId) {
      // Try to get from usuario object
      const usuarioData = localStorage.getItem('usuario');
      if (usuarioData) {
        try {
          const usuarioObj = JSON.parse(usuarioData);
          usuarioId = usuarioObj.usuarioId || (usuarioObj.usuario && usuarioObj.usuario.usuarioId);
        } catch (e) {
          console.error('Erro ao analisar dados do usuário:', e);
        }
      }
    }
    
    return usuarioId || null;
  }

  formatCurrency(value: number | string): string {
    if (value === null || value === undefined) return 'Não informado';
    
    const numberValue = typeof value === 'string' ? parseFloat(value) : value;
    

    if (isNaN(numberValue)) return 'Não informado';

    return numberValue.toLocaleString('pt-BR', { 
      style: 'currency', 
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  // Add this method to format CPF with mask
  formatCpf(cpf: string | null | undefined): string {
    if (!cpf) return 'Não informado';
    
    // Remove any non-numeric characters
    const numericOnly = cpf.replace(/\D/g, '');
    
    // Check if it's a valid length for CPF (11 digits)
    if (numericOnly.length !== 11) {
      return cpf; // Return original if not standard length
    }

    return numericOnly.replace(
      /(\d{3})(\d{3})(\d{3})(\d{2})/,
      '$1.$2.$3-$4'
    );
  }

  forceRefresh(): void {
    this.totalPages = {}; // Limpa todo o cache em memória
    // this.paginaAtual = 1; // Opcional: resetar para a primeira página ao forçar atualização
    this.loadAllConsumers(); // Recarrega os dados da página atual (ou primeira, se resetado)
    
    this.filters = { // Limpa os filtros de busca
      nome: '',
      cpf: '',
      matricula: ''
    };
    this.searchTerm = '';
    
    Swal.fire({
      icon: 'success',
      title: 'Dados atualizados!',
      showConfirmButton: false,
      timer: 1500
    });
  }

  // Retorna todas as chaves do objeto
  getAllKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }

  // Verifica se uma chave é de mídia
  isMediaKey(key: string): boolean {
    return this.mediaKeys.includes(key);
  }

  // Retorna as fotos do consumidor
  getFotos(consumer: any): string[] {
    return consumer?.fotos || [];
  }

  // Retorna as assinaturas do consumidor
  getAssinaturas(consumer: any): string[] {
    return consumer?.assinaturas || [];
  }

  // Verifica se há mídia no consumidor
  hasMedia(consumer: any): boolean {
    return this.getFotos(consumer).length > 0 || this.getAssinaturas(consumer).length > 0;
  }

  // Formata o valor para exibição legível
  formatField(value: any): string {
    if (value === null || value === undefined) return 'Não preenchido';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return '[objeto]';
      }
    }
    return value;
  }

  // Adicione este método:
  irParaEdicao(consumer: any): void {
    if (consumer && consumer.idJuncao) {
      this.consumidorService.consultarConsumidorPorId(consumer.idJuncao).subscribe({
        next: (dadosCompletos) => {
          localStorage.setItem('formEdite', JSON.stringify(dadosCompletos));
          localStorage.setItem('idJuncao', consumer.idJuncao);
          this.router.navigate(
            ['/admin/editar-consumidor', consumer.recadastroId || consumer.idJuncao],
            { queryParams: { idJuncao: consumer.idJuncao } }
          );
        },
        error: () => {
          Swal.fire('Erro', 'Não foi possível carregar os dados completos do consumidor.', 'error');
        }
      });
    }
  }
}